import {
  ActivityType,
  activityDetails,
  cleanHtmlContent,
  getActivityScript,
  getFormattedActivity,
  lookupExampleWord,
  parseHTMLToSections,
} from '../utils';
import { Slide } from './types';

function isActivityType(type: string): type is ActivityType {
  return Object.values(ActivityType).includes(type as ActivityType);
}

/** Activities that get a single "refer to plan" slide instead of per-item content slides. */
const REFER_TO_PLAN_ACTIVITIES: ActivityType[] = [
  ActivityType.LETTER_SOUND_SPELLING_DRILL,
  ActivityType.ORAL_SEGMENTING_DRILL,
  ActivityType.SPELLING_DRILL,
  ActivityType.SPELLING_WORD_CHAIN,
  ActivityType.SENTENCE_DICTATION,
  ActivityType.ORAL_BLENDING_DRILL,
];

function extractWordSum(value: string): string {
  const parts = value.split('=');
  return parts[0].trim();
}

export function parseContentToSlides(html: string): Slide[] {
  const sections = parseHTMLToSections(html);
  const slides: Slide[] = [];

  sections.forEach((section) => {
    if (section.type === 'wordMatrix') {
      const details = activityDetails[ActivityType.WORD_MATRIX];
      const script = section.script || details.script;

      slides.push({
        type: 'activity-title',
        heading: 'Word Matrix',
        activityType: ActivityType.WORD_MATRIX,
        script,
        icon: details.icon,
      });

      const baseWord =
        section.content
          .find((c) => (c.value as string).startsWith('base:'))
          ?.value?.toString()
          .replace('base:', '') || '';

      const parsePipeColumns = (raw: string): string[][] => {
        if (!raw) return [[]];
        return raw.split('|').map((col) => col.split(',').filter(Boolean));
      };

      const prefixColsItem = section.content.find((c) =>
        (c.value as string).startsWith('prefixColumns:'),
      );
      const suffixColsItem = section.content.find((c) =>
        (c.value as string).startsWith('suffixColumns:'),
      );

      const prefixColumns = prefixColsItem
        ? parsePipeColumns(
            (prefixColsItem.value as string).replace('prefixColumns:', ''),
          )
        : [[]];
      const suffixColumns = suffixColsItem
        ? parsePipeColumns(
            (suffixColsItem.value as string).replace('suffixColumns:', ''),
          )
        : [[]];

      slides.push({
        type: 'word-matrix',
        heading: 'Make as many real words as you can',
        activityType: ActivityType.WORD_MATRIX,
        script,
        icon: details.icon,
        wordMatrixData: { baseWord, prefixColumns, suffixColumns },
      });
    } else if (section.type === 'wordSums') {
      const details = activityDetails[ActivityType.WORD_SUMS];
      const script = section.script || details.script;

      slides.push({
        type: 'activity-title',
        heading: 'Word Sums',
        activityType: ActivityType.WORD_SUMS,
        script,
        icon: details.icon,
      });

      section.content.forEach((item) => {
        const value = cleanHtmlContent(item.value as string);
        if (!value) return;
        slides.push({
          type: 'content',
          heading: extractWordSum(value) + ' =',
          activityType: ActivityType.WORD_SUMS,
          script,
          icon: details.icon,
        });
      });
    } else if (isActivityType(section.type)) {
      const activityType = section.type;
      const details = activityDetails[activityType];
      const activityTitle = getFormattedActivity(activityType);
      const script = section.script
        ? cleanHtmlContent(section.script)
        : getActivityScript(activityType);

      slides.push({
        type: 'activity-title',
        heading: activityTitle,
        subheading: section.skill,
        activityType,
        script,
        icon: details.icon,
      });

      if (REFER_TO_PLAN_ACTIVITIES.includes(activityType)) {
        slides.push({
          type: 'refer-to-plan',
          heading: activityTitle,
          activityType,
          script,
          icon: details.icon,
        });
      } else {
        section.content.forEach((item) => {
          const rawValue = item.value as string;
          const value = cleanHtmlContent(rawValue);
          if (!value) return;

          if (activityType === ActivityType.DECODING_WORD_CHAIN) {
            value
              .split(',')
              .map((w) => w.trim())
              .filter(Boolean)
              .forEach((word) => {
                slides.push({
                  type: 'content',
                  heading: word,
                  activityType,
                  script,
                  icon: details.icon,
                });
              });
          } else if (activityType === ActivityType.SOUND_RECOGNITION_DRILL) {
            const pipeIdx = value.indexOf('|');
            const spelling = pipeIdx >= 0 ? value.substring(0, pipeIdx) : value;
            const pronunciation =
              pipeIdx >= 0 ? value.substring(pipeIdx + 1) : undefined;
            let image: string | undefined;
            let subtext: string | undefined;

            const exampleWord = lookupExampleWord(spelling, pronunciation);
            if (exampleWord) {
              image = `/game_assets/definition_images/${exampleWord}.png`;
              subtext = exampleWord;
            }

            slides.push({
              type: 'content',
              heading: spelling,
              activityType,
              script,
              icon: details.icon,
              image,
              subtext,
            });
          } else {
            slides.push({
              type: 'content',
              heading: value,
              activityType,
              script,
              icon: details.icon,
            });
          }
        });
      }
    }
  });

  return slides;
}
